<script setup lang="ts">
import type { NavigationBarProps } from './navigation-bar';
import TLogo from '../Logo/TLogo.vue';
import NavigationBarMenuItem from './items/NavigationBarMenuItem.vue';
import NavigationBarActionItem from './items/NavigationBarActionItem.vue';
import NavigationBarProfileItem from './items/NavigationBarProfileItem.vue';
import IconButton from '../IconButton/IconButton.vue';
import Popover from '../Popover/Popover.vue';
import NavigationBarProfilePopoverContent from './profile/NavigationBarProfilePopoverContent.vue';

const props = defineProps<NavigationBarProps>();

const emits = defineEmits<{
  toggleOpen: [];
  navigateTo: [href: string, openInNewTab: boolean];
}>();

const isItemActive = (item: {
  href?: string;
  exactMatch?: boolean;
  active?: boolean;
}) => {
  if (item.active) return true;

  if (!props.currentRoute || !item.href) return false;

  const routePath = props.currentRoute.split('?')[0].split('#')[0];

  if (item.exactMatch) {
    return routePath === item.href;
  }

  // Add '/' to ensure we match full path segments only
  return routePath === item.href || routePath.startsWith(`${item.href}/`);
};
</script>

<template>
  <div
    class="flex flex-col overflow-hidden bg-slate-50 transition-all duration-200 ease-out"
    :class="[props.isOpen ? 'w-60' : 'w-16']"
  >
    <div
      class="mb-2 flex min-w-0 flex-1 flex-col overflow-hidden"
      :class="[props.isOpen ? 'items-start' : 'items-center']"
    >
      <div
        class="flex w-full"
        :class="[
          props.isOpen ? 'items-center p-4' : 'items-center justify-center p-2',
        ]"
      >
        <div
          class="flex-1 overflow-hidden transition-all duration-200 ease-out"
          :class="[
            props.isOpen ? 'w-full opacity-100' : 'w-0 max-w-0 opacity-0',
          ]"
        >
          <TLogo variant="full" height="25" />
        </div>

        <IconButton
          :icon="props.isOpen ? 'chevron_left' : 'chevron_right'"
          variant="ghost-neutral"
          @click="emits('toggleOpen')"
        />
      </div>

      <div
        class="mb-2 flex w-full shrink-0 items-center justify-center transition-all duration-200 ease-out"
        :class="[
          props.isOpen
            ? 'mt-0 h-0 max-h-0 opacity-0'
            : 'mt-4 h-10 max-h-10 opacity-100',
        ]"
      >
        <TLogo variant="icon" height="34" />
      </div>
      <div
        class="flex w-full flex-col gap-2 overflow-x-hidden overflow-y-auto pb-2"
      >
        <div
          v-for="item in props.items"
          :key="item.id"
          class="flex items-center justify-center"
          :class="props.isOpen ? 'px-4' : 'min-w-16 px-2'"
        >
          <NavigationBarMenuItem
            v-if="item.type === 'menu-item'"
            :item="item"
            :compact="!props.isOpen"
            :is-active="isItemActive(item)"
            @navigate-to="
              (href, openInNewTab) => emits('navigateTo', href, openInNewTab)
            "
          />
          <NavigationBarActionItem
            v-if="item.type === 'action'"
            :item="{ ...item }"
            :compact="!props.isOpen"
          />
          <div
            v-if="item.type === 'separator'"
            class="h-[1px] w-full bg-slate-200"
          />
          <div
            v-if="item.type === 'label'"
            class="truncate text-[11px] font-bold text-slate-400 uppercase transition-all duration-200 ease-out"
            :class="[
              props.isOpen ? 'mt-2 w-full' : 'mt-0 h-0 max-h-0 w-0 max-w-0',
            ]"
          >
            {{ item.label }}
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex w-full min-w-0 items-center justify-center"
      :class="[props.isOpen ? 'p-4' : 'p-2']"
    >
      <Popover placement="right-end">
        <template #trigger>
          <NavigationBarProfileItem
            :label="props.profileLabel"
            :username="props.username"
            :compact="!props.isOpen"
            :trial-days-left="props.trialDaysLeft"
            :trial-days-total="props.trialDaysTotal"
            :grace-days-left="props.graceDaysLeft"
            :grace-days-total="props.graceDaysTotal"
          />
        </template>
        <template #content="{ close }">
          <NavigationBarProfilePopoverContent
            :profile-items="props.profileItems"
            :imprint-items="props.imprintItems"
            @navigate-to="
              (href) => {
                close();
                emits('navigateTo', href, false);
              }
            "
          />
        </template>
      </Popover>
    </div>
  </div>
</template>
